'use client';

import { Input } from '@savvy-kit/components/input';
import { Label } from '@savvy-kit/components/label';
import { cn } from '@savvy-kit/lib/utils';

import type { NumberFilterMeta, NumberOperator } from '../../types/filter-meta';
import type { NumberFilterValue } from '../../types/filter-values';
import { useOperatorOptions } from './hooks/use-operator-options';
import { FilterActions } from './shared/filter-actions';
import { FilterOperatorSelect } from './shared/filter-operator-select';

export type NumberFilterProps = {
  readonly filterMeta?: NumberFilterMeta;
  readonly range?: { min: number; max: number };
  readonly value: NumberFilterValue | undefined;
  readonly onChange: (value: NumberFilterValue | undefined) => void;
  readonly className?: string;
};

export function NumberFilter({ filterMeta, range, value, onChange, className }: NumberFilterProps) {
  const operators = useOperatorOptions('number', filterMeta);
  const operator: NumberOperator = value?.operator ?? 'between';

  const singleValue = value && value.operator !== 'isEmpty' && value.operator !== 'between' ? value.value : '';
  const minValue = value && value.operator === 'between' ? value.min : '';
  const maxValue = value && value.operator === 'between' ? value.max : '';

  return (
    <div className={cn('flex flex-col gap-3 p-3', className)}>
      {range && (
        <p className="text-xs text-muted-foreground">
          Range: {range.min} – {range.max}
        </p>
      )}

      <FilterOperatorSelect
        id="number-filter-operator"
        value={operator}
        options={operators}
        onChange={(op) => (op === 'isEmpty' ? onChange({ operator: 'isEmpty' }) : onChange(undefined))}
      />

      {operator !== 'isEmpty' && operator !== 'between' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="number-filter-value">Value</Label>
          <Input
            id="number-filter-value"
            type="number"
            value={singleValue}
            onChange={(e) => {
              const num = parseFloat(e.target.value);
              onChange(!isNaN(num) ? { operator, value: num } : undefined);
            }}
            placeholder="Enter number..."
          />
        </div>
      )}

      {operator === 'between' && (
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="number-filter-min">Min</Label>
            <Input
              id="number-filter-min"
              type="number"
              value={minValue}
              onChange={(e) => {
                const min = parseFloat(e.target.value);
                const max = typeof maxValue === 'number' ? maxValue : min;
                onChange(!isNaN(min) ? { operator: 'between', min, max } : undefined);
              }}
              placeholder={range ? String(range.min) : 'Min'}
            />
          </div>
          <span className="pb-2 text-sm text-muted-foreground">–</span>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="number-filter-max">Max</Label>
            <Input
              id="number-filter-max"
              type="number"
              value={maxValue}
              onChange={(e) => {
                const max = parseFloat(e.target.value);
                const min = typeof minValue === 'number' ? minValue : max;
                onChange(!isNaN(max) ? { operator: 'between', min, max } : undefined);
              }}
              placeholder={range ? String(range.max) : 'Max'}
            />
          </div>
        </div>
      )}

      {value && <FilterActions onClear={() => onChange(undefined)} />}
    </div>
  );
}
